import { Component, Inject, AfterViewInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog'
import { SharedModule } from '@app/shared/shared.module'
import { ScopeUiDatepickerComponent } from '@app/shared/components/ui-components/scope-ui-datepicker/scope-ui-datepicker.component'
import { ScopeUiAutocompleteComponent } from '@app/shared/components/ui-components/scope-ui-autocomplete/scope-ui-autocomplete.component'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { CKEditorModule } from '@ckeditor/ckeditor5-angular'
import { ScopeUiInputComponent } from '@shared/components/ui-components/scope-ui-input/scope-ui-input.component'
import { NgxSliderModule } from '@angular-slider/ngx-slider'
import { NgxMaskDirective } from 'ngx-mask'
import { ScopeUiDropdownComponent } from '@shared/components/ui-components/scope-ui-dropdown/scope-ui-dropdown.component'
import { Store } from '@ngrx/store'
import { QuestionType } from '@core/model/enums/question-type.enum'
import { FormControl } from '@angular/forms'
import { FormulaBuilderComponent } from '@shared/components/formula-builder/formula-builder.component'
import { Scenario } from '@app/features/scope-overview/model/scenario.model'
import { Observable } from 'rxjs'
import { ScopeUiChipsComponent } from '@shared/components/ui-components/scope-ui-chips/scope-ui-chips.component'
import { ScopeUiRadioComponent } from '@shared/components/ui-components/scope-ui-radio/scope-ui-radio.component'
import { ScopeUiCheckboxComponent } from '@shared/components/ui-components/scope-ui-checkbox/scope-ui-checkbox.component'
import { LibraryManagementNgrxModule } from '@app/features/library-management/library-management-ngrx.module'
import { ScopeUiTableComponent } from '@shared/components/ui-components/scope-ui-table/scope-ui-table.component'
import { MatTableDataSource } from '@angular/material/table'
import { SvgIconComponent } from '@shared/components/svg-icon/svg-icon.component'
import { FeeItem } from '@app/features/scope-overview/model/fee-item.model'
import { ScopingSelectors } from '@app/features/scoping/store/selectors/scoping.selector'
import { PromptAutocompleteComponent } from '@app/features/company-management/components/prompt-autocomplete/prompt-autocomplete.component'
import { FeePromptOption } from '@app/features/scope-overview/model/fee-prompt.model'
import { ScopeActions } from '@app/features/scoping/store/actions/scoping.actions'
import { plainToInstance } from 'class-transformer'
import { CompanyManagementSelectors } from '@app/features/company-management/store/selectors/company-management.selectors'
import { PromptModalConfig } from '@app/features/company-management/models/prompt-modal-config'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { LibraryDeliverableEntryDetails } from '@app/features/library-management/store/models/deliverable/library-deliverable-entry-details.model'
import {
  selectAllDeliverableEntryDetails,
  selectDeliverableEntryDetailsLastPage,
  selectDeliverableEntryDetailsLoading,
} from '@app/features/library-management/store/selectors/library-deliverable-entry-details.selectors'
import { LibraryDeliverableEntryDetailsActions } from '@app/features/library-management/store/models/deliverable/library-deliverable-entry-details.action.interface'
import { LanguageService } from '@core/service/language.service'
import { LibraryDeliverableEntryDetailsService } from '@app/features/library-management/services/library-deliverable-entry-details.service'
import { untilDestroyed } from '@shared/utils/utils'
import { SortDirection } from '@app/core/model/enums/sort-direction.enum'

@Component({
  selector: 'add-fee-prompt-modal',
  standalone: true,
  imports: [
    CommonModule,
    LibraryManagementNgrxModule,
    ScopeUiDatepickerComponent,
    ScopeUiAutocompleteComponent,
    SharedModule,
    MatDatepickerModule,
    CKEditorModule,
    ScopeUiInputComponent,
    NgxSliderModule,
    NgxMaskDirective,
    ScopeUiDropdownComponent,
    FormulaBuilderComponent,
    ScopeUiChipsComponent,
    ScopeUiRadioComponent,
    ScopeUiCheckboxComponent,
    PromptAutocompleteComponent,
    ScopeUiTableComponent,
    SvgIconComponent,
    MatButtonToggleModule,
  ],
  templateUrl: './add-fee-prompt-modal.component.html',
  styleUrls: ['./add-fee-prompt-modal.component.scss'],
})
export class AddFeePromptModalComponent implements AfterViewInit {
  private readonly destroy$

  title = `Configure Fee prompt`
  instructions = `1. Set up a prompt and define the logic when it appears.\n 2. Let users create a custom fee or create a selection of pre-set options \n 3. Allow to assign to deliverables or keep it at scope level`
  questionTextLabel = `Enter a prompt for selecting fees`
  feeNameLabel = `Fee name`
  feeDescriptionLabel = `Fee description`
  configureTitle = `Set when this Fee Selection appears`
  questionText: string
  feeName: string

  feeDescription: string
  required = false
  visible = true
  showAlways = true
  showOnCondition = false
  condition: string
  step = 1
  page = 0
  scenario: Scenario
  conditionControl = new FormControl()
  feeItems$: Observable<FeeItem[]>
  isLoading$: Observable<boolean>
  selectedFeeItems: FeePromptOption[] = []
  selectedDeliverables: LibraryDeliverableEntryDetails[] = []
  dataSource = new MatTableDataSource<FeePromptOption>([])
  deliverableDataSource = new MatTableDataSource<LibraryDeliverableEntryDetails>([])
  sortedColumn: string | null = null
  sortDirection = SortDirection.NONE
  displayedColumns: string[] = ['pin', 'name', 'delete']
  displayedDeliverablesColumns: string[] = ['pin', 'deliverable_name', 'discipline_name', 'delete']
  updateLoading$: Observable<boolean>
  typeControl = new FormControl('INCREASE')
  editPage = false
  enableDeliverables = false
  deliverableEntryDetails$: Observable<LibraryDeliverableEntryDetails[]>
  isLoadingDeliverable$: Observable<boolean>
  lastPage$: Observable<boolean>

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PromptModalConfig,
    private store: Store,
    private lang: LanguageService,
    public dialog: MatDialog,
    private libraryDeliverableEntryDetailsService: LibraryDeliverableEntryDetailsService
  ) {
    this.destroy$ = untilDestroyed()
    this.updateLoading$ = this.store.select(CompanyManagementSelectors.selectLoadingAddUpdateScenarioQuestion)
    this.scenario = this.data.scenario
    if (data.question) {
      this.questionText = data.question.displayText
      this.required = data.question.mandatory
      this.showAlways = !data.question.hidden && !data.question.displayCondition
      this.showOnCondition = !!data.question.displayCondition
      this.condition = data.question.displayCondition
      this.constructFeeData(data.question)
      this.selectedFeeItems = data.question.feePrompt?.feePromptOptions
      this.editPage = !!(data.question.feePrompt?.feePromptOptions || data.question.fieldLabel)
    }
  }

  ngAfterViewInit(): void {
    this.refreshTable()
  }

  onSort(column: string): void {
    this.toggleColumnSortDirection(column)
    this.orderData()
  }

  onSortDeliverable(column: string): void {
    this.toggleColumnSortDirection(column)
    this.orderDeliverableData()
  }

  toggleColumnSortDirection(column: string) {
    if (this.sortedColumn === column) {
      this.sortDirection = this.getSortDirection(this.sortDirection)
    } else {
      this.sortedColumn = column
      this.sortDirection = SortDirection.ASC
    }
  }

  getSortDirection(direction: SortDirection) {
    if (direction === SortDirection.ASC) return SortDirection.DESC
    if (direction === SortDirection.DESC) {
      this.sortedColumn = null
      return SortDirection.NONE
    }
    return SortDirection.ASC
  }

  private orderDeliverableData(): void {
    const data = this.selectedDeliverables ? [...this.selectedDeliverables] : []

    if (!this.sortedColumn || !this.sortDirection) {
      this.deliverableDataSource.data = data
      return
    }

    const direction = this.sortDirection
    const column = this.sortedColumn

    data.sort((a, b) => {
      let valA = ''
      let valB = ''

      if (column === 'deliverable_name') {
        valA = (a.libraryItemName || '').toLowerCase()
        valB = (b.libraryItemName || '').toLowerCase()
      } else if (column === 'discipline_name') {
        valA = (a.disciplineName || '').toLowerCase()
        valB = (b.disciplineName || '').toLowerCase()
      }

      if (valA > valB) return direction === 'asc' ? 1 : -1
      if (valA < valB) return direction === 'asc' ? -1 : 1
      return 0
    })

    this.deliverableDataSource.data = data
  }

  private orderData(): void {
    const data = this.selectedFeeItems ? [...this.selectedFeeItems] : []

    if (!this.sortedColumn || !this.sortDirection) {
      this.dataSource.data = data
      return
    }

    const direction = this.sortDirection
    const column = this.sortedColumn

    data.sort((a, b) => {
      let valA = ''
      let valB = ''

      if (column === 'name') {
        valA = (a.name || '').toLowerCase()
        valB = (b.name || '').toLowerCase()
      }

      if (valA > valB) return direction === 'asc' ? 1 : -1
      if (valA < valB) return direction === 'asc' ? -1 : 1
      return 0
    })

    this.dataSource.data = data
  }

  private refreshTable() {
    if (this.selectedFeeItems) {
      this.dataSource.data = [...this.selectedFeeItems]
      this.orderData()
    }
  }

  private refreshDeliverableTable() {
    this.deliverableDataSource.data = this.selectedDeliverables ? [...this.selectedDeliverables] : []
    this.orderData()
  }

  updateSelectedFees(selected: FeeItem): void {
    this.selectedFeeItems = [
      ...this.selectedFeeItems,
      plainToInstance(FeePromptOption, { feeId: selected.id, name: selected.name }),
    ]
    this.refreshTable()
  }

  fetchDeliverables(event: { page: number; searchString: string }): void {
    this.store.dispatch(
      LibraryDeliverableEntryDetailsActions.loadDeliverableEntryDetails({
        page: event.page,
        pageSize: 10,
        filter: { libraryItemName: event.searchString },
      })
    )
  }

  updateSelectedDeliverables(selected: any): void {
    this.selectedDeliverables = [...this.selectedDeliverables, selected]
    this.refreshDeliverableTable()
  }

  pinDeliverable(deliverable: LibraryDeliverableEntryDetails) {
    const updated = { ...deliverable, required: !deliverable.required }
    this.selectedDeliverables = this.selectedDeliverables.map((d) =>
      d.libraryDeliverableEntryId === deliverable.libraryDeliverableEntryId ? updated : d
    )
    this.refreshDeliverableTable()
  }

  deleteDeliverable(deliverable: LibraryDeliverableEntryDetails) {
    this.selectedDeliverables = this.selectedDeliverables.filter(
      (d) => d.libraryDeliverableEntryId !== deliverable.libraryDeliverableEntryId
    )
    this.refreshDeliverableTable()
  }

  goToStep(step: number) {
    if (step === 2 || step === 3) {
      this.submit()
    } else {
      this.store.dispatch(ScopeActions.getAllFeeItems())
      this.feeItems$ = this.store.select(ScopingSelectors.selectAllFeeItems)
      this.isLoading$ = this.store.select(ScopingSelectors.selectLoadingFees)
      this.deliverableEntryDetails$ = this.store.select(selectAllDeliverableEntryDetails)
      this.isLoadingDeliverable$ = this.store.select(selectDeliverableEntryDetailsLoading)
      this.lastPage$ = this.store.select(selectDeliverableEntryDetailsLastPage)
      if (this.feeName) {
        this.step = 3
      } else {
        ++this.step
      }
    }
  }

  optionFunction = (option: FeeItem) => {
    return `<div class='item-title'>${option.name}</div>`
  }

  deliverableOptionFunction = (option: LibraryDeliverableEntryDetails) => {
    return `<div class='item-title'>${option.libraryItemName}</div>
            <div class='item-description'>
              Discipline: ${option.disciplineName || '-'}
            </div>`
  }

  disableNextStep(): boolean {
    return (
      !this.questionText ||
      (this.showOnCondition && (!this.condition || this.conditionControl.hasError('invalid'))) ||
      (this.step === 2 && !this.selectedFeeItems?.length) ||
      (this.step === 3 && (!this.feeName || (this.enableDeliverables && this.selectedDeliverables?.length == 0)))
    )
  }

  submit() {
    const deliverablePrompt = {
      deliverablePromptOptions: this.enableDeliverables
        ? this.selectedDeliverables.map((deliverable) => ({
            deliverableId: deliverable.libraryDeliverableEntryId,
            libraryItemName: deliverable.libraryItemName,
            required: deliverable.required,
          }))
        : [],
    }

    const payload = {
      id: this.data.question?.id,
      displayText: this.questionText,
      mandatory: this.required,
      displayCondition: this.condition,
      type: QuestionType.FEE,
      deliverablePrompt,
    }

    const feePrompt = { feePromptOptions: this.selectedFeeItems }
    const stepPayload =
      this.step === 2
        ? { feePrompt }
        : {
            defaultValue: this.typeControl.value == 'INCREASE' ? 1 : -1,
            fieldLabel: this.feeName,
          }

    this.data.confirmCallback({ ...payload, ...stepPayload })
  }

  removeFeeItem(feeItem: FeePromptOption) {
    this.selectedFeeItems = this.selectedFeeItems.filter((d) => d.feeId !== feeItem.feeId)
    this.refreshTable()
  }

  pinFeeItem(feeItem: FeePromptOption) {
    const updated = { ...feeItem, required: !feeItem.required }
    this.selectedFeeItems = this.selectedFeeItems.map((d) => (d.feeId === feeItem.feeId ? updated : d))
    this.refreshTable()
  }

  private constructFeeData(question) {
    this.feeName = question.fieldLabel
    this.typeControl = question?.defaultValue == 1 ? new FormControl('INCREASE') : new FormControl('DECREASE')
    if (question.deliverablePrompt) {
      const deliverableIds = question.deliverablePrompt.deliverablePromptOptions.map((option) => option.deliverableId)
      this.enableDeliverables = !!deliverableIds?.length
      if (deliverableIds?.length)
        this.libraryDeliverableEntryDetailsService
          .getDeliverableEntryDetailsByIds(deliverableIds)
          .pipe(this.destroy$())
          .subscribe({
            next: (deliverables: LibraryDeliverableEntryDetails[]) => {
              question.deliverablePrompt.deliverablePromptOptions.forEach((option) => {
                const match = deliverables.find((del) => del.libraryDeliverableEntryId === option.deliverableId)
                if (match) match.required = option.required
              })
              this.selectedDeliverables = deliverables
              this.refreshDeliverableTable()
            },
            error: () => console.error('Can not receive Library deliverables'),
          })
      else {
        this.selectedDeliverables = []
        this.refreshDeliverableTable()
      }
    }
  }

  isSelected = (option: FeeItem) => {
    return this.selectedFeeItems?.some((selected) => selected.feeId === option.id)
  }

  isSelectedDeliverable = (option: LibraryDeliverableEntryDetails) => {
    return this.selectedDeliverables?.some(
      (selected) => selected.libraryDeliverableEntryId === option.libraryDeliverableEntryId
    )
  }

  getPinTooltip = (feeItem: FeePromptOption) =>
    feeItem.required ? 'Pinned. Unpin to make a fee optional' : 'Unpinned. Pin a fee so it is always included'

  getDeliverablePinTooltip = (deliverable: LibraryDeliverableEntryDetails) =>
    deliverable.required
      ? `Pinned. Unpin to make a ${this.lang.get('deliverable|l')} optional`
      : `Unpinned. Pin a ${this.lang.get('deliverable|l')} so it is always included`
}
